/**
 * Org service — single source of truth for the current org context in the
 * UI.
 *
 * Backed by signals so components can react without managing
 * subscriptions. Reads from two places:
 *
 *  - `users/{uid}.activeOrgId` + `.orgIds` for the org switcher (mirrored
 *    by `syncOrgMemberClaims` on every member doc write).
 *  - `orgs/{activeOrgId}` for the active org's metadata.
 *
 * Member docs and department lists are NOT subscribed here — settings
 * sub-tabs query them on demand via `collection()` snapshots so the org
 * service stays cheap (one user-doc + one org-doc subscription).
 *
 * Mutations go via callable Cloud Functions — never write Firestore docs
 * directly. The trigger keeps the user mirror up to date.
 */

import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { doc, DocumentReference, Firestore, onSnapshot, setDoc } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';

import { FIREBASE_HANDLES } from '../firebase/firebase.providers';
import { AuthService } from '../firebase/auth.service';
import { hasPermission } from './permissions';
import { ViewAsService } from './view-as.service';
import type {
  JoinRequestMirror,
  Org,
  OrgFeatureName,
  OrgPermission,
  OrgRole,
  UserOrgFields,
} from './orgs.types';
import { parseJoinRequest } from './join-request-parse';

interface UserOrgState {
  readonly orgIds: readonly string[];
  readonly activeOrgId: string | null;
  readonly pendingActivation: boolean;
  readonly joinRequest: JoinRequestMirror | null;
}

/** Where a tab remembers the org it was sent to. */
const TAB_ORG_KEY = 'ghosts.tabOrgId';

/**
 * Is this a thing Firestore will accept as a document id?
 *
 * ⚠️ The tab's org arrives from the URL (`?actAs=`), and `doc(db, 'orgs', id)`
 * THROWS on an id containing a slash — `orgs/a/b` is a collection path, not a
 * document one. Unchecked, `?actAs=a%2Fb` would break the page from inside an
 * effect and then persist, so every reload of that tab broke it again.
 * Rejecting it leaves the tab on the account's org, which is a working page.
 */
function isDocumentId(value: string): boolean {
  return value.length > 0 && value.length <= 1500 && !value.includes('/') && !/^\.\.?$/.test(value);
}

/** Storage can throw or come back empty (private windows, cleared data). */
function readTabOrg(): string | null {
  try {
    const stored = sessionStorage.getItem(TAB_ORG_KEY);
    return stored && isDocumentId(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Which org doc a listener reads, and whether the token may read it. */
interface OrgDocKey {
  readonly id: string;
  readonly readable: boolean;
}

function sameOrgDocKey(a: OrgDocKey | null, b: OrgDocKey | null): boolean {
  return a === b || (!!a && !!b && a.id === b.id && a.readable === b.readable);
}

const EMPTY_USER_STATE: UserOrgState = {
  orgIds: [],
  activeOrgId: null,
  pendingActivation: false,
  joinRequest: null,
};

@Injectable({ providedIn: 'root' })
export class OrgService {
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly handles = inject(FIREBASE_HANDLES);
  private readonly viewAs = inject(ViewAsService);
  private readonly db: Firestore = this.handles.firestore;
  private readonly fns: Functions = this.handles.functions;

  /**
   * This TAB's org, when it was sent to one (#255).
   *
   * ⚠️ The persisted `activeOrgId` is per-ACCOUNT and live: every tab
   * subscribes to the same user doc, so a second tab switching org drags the
   * first one with it, mid-review. That is fine for a person working in one
   * org and useless for a ghost admin triaging six — which is what the review
   * board's links hand out. So a tab can hold its own, and this wins.
   *
   * In `sessionStorage` because sessionStorage is per tab, which is exactly
   * the scope of the claim. (A tab opened FROM this one inherits the store —
   * browsers copy it — so a plain link carries the context onward, and a
   * link carrying its own `actAs` overwrites it on arrival.)
   */
  private readonly _tabOrgId = signal<string | null>(readTabOrg());

  private readonly _userState = signal<UserOrgState>(EMPTY_USER_STATE);
  private readonly _userLoaded = signal(false);
  /** Successful `users/{uid}` snapshots this session; 0 before the first. */
  private readonly _userDocSeq = signal(0);
  private readonly _activeOrg = signal<Org | null>(null);
  /**
   * What `_activeOrg` was loaded FOR: the org id, and whether the token could
   * read it then. `activeOrgId` moves the moment a tab switches org, but the
   * doc arrives a snapshot later, and until the org effect runs `_activeOrg`
   * still holds the previous org's doc. A reader that must not judge one org
   * by another's doc waits for the two to match (`activeOrgDocId`).
   */
  private readonly _activeOrgDocKey = signal<OrgDocKey | null>(null);
  /** Signed-in user's role in the active org. Owner > admin > member > viewer. */
  private readonly _activeRole = signal<OrgRole | null>(null);

  /** Available org IDs the signed-in user belongs to. */
  readonly orgIds = computed(() => this._userState().orgIds);
  /**
   * Currently selected org ID, if any — this tab's override first, then the
   * account's persisted choice. Everything downstream (`can`, the guards,
   * every screen's query) reads this one signal, which is what lets a tab
   * act as another org without a single screen knowing about the idea.
   */
  readonly activeOrgId = computed(
    () => (this.isInternalAccount() ? this._tabOrgId() : null) ?? this._userState().activeOrgId,
  );

  /** The org this tab was sent to, or null when it follows the account. */
  readonly tabOrgId = computed(() => (this.isInternalAccount() ? this._tabOrgId() : null));

  /**
   * ⚠️ Checked on every read, not just when the override is written.
   *
   * `sessionStorage` outlives a sign-out in the same tab, so without this a
   * ghost admin's borrowed context could be inherited by whoever signs in
   * next — who would then watch the app query an org they have no business
   * in and fail at every turn. The override also clears on sign-out below;
   * this is the belt to that pair of braces.
   */
  private isInternalAccount(): boolean {
    return this.auth.claims().internalAdmin === true;
  }
  /** Active org metadata (null while loading or when no org selected). */
  readonly activeOrg = this._activeOrg.asReadonly();

  /**
   * The active org and whether the signed-in token may read its doc (`get`
   * needs the org in the `orgRoles` claim, or staff). The org-doc listener
   * is keyed on both: a listener the rules refused ends for good, so when a
   * refreshed token brings the role (a fresh invite), the doc is read again
   * instead of staying missing for the rest of the session. A token refresh
   * that changes neither keeps the listener.
   */
  private readonly orgDocKey = computed<OrgDocKey | null>(
    () => {
      const id = this.activeOrgId();
      if (!id) return null;
      const claims = this.auth.claims();
      return { id, readable: claims.internalAdmin === true || !!claims.orgRoles?.[id] };
    },
    { equal: sameOrgDocKey },
  );

  /**
   * Which org `activeOrg` belongs to; null while none is loaded for the
   * current org and access. Derived, so it drops to null the moment the key
   * moves (a switch, or the role arriving), before the listener catches up.
   */
  readonly activeOrgDocId = computed(() => {
    const loaded = this._activeOrgDocKey();
    return loaded && sameOrgDocKey(loaded, this.orgDocKey()) ? loaded.id : null;
  });
  /** Caller's role in the active org. */
  readonly activeRole = this._activeRole.asReadonly();
  /** True between member-doc creation and first sign-in. */
  readonly pendingActivation = computed(() => this._userState().pendingActivation);
  /** The person's join request, or null when there is none or it is malformed. */
  /** True once the first `users/{uid}` snapshot (or its error) has arrived. */
  readonly userLoaded = this._userLoaded.asReadonly();
  /**
   * Bumped by every successful `users/{uid}` snapshot (0 before the first,
   * and when it could not be read). `MembershipSessionService` refreshes the
   * ID token at most once per snapshot, so a token that stays out of step
   * with the mirror cannot make it loop.
   */
  readonly userDocSeq = this._userDocSeq.asReadonly();
  readonly joinRequest = computed<JoinRequestMirror | null>(() => this._userState().joinRequest);

  private unsubscribeUser: (() => void) | null = null;
  private unsubscribeOrg: (() => void) | null = null;

  /** The account the user effect last saw; `undefined` until it resolves. */
  private lastUid: string | null | undefined = undefined;

  constructor() {
    // Bind/unbind user-doc subscription to the signed-in user.
    effect(() => {
      const user = this.auth.user();
      // `null` is signed OUT; `undefined` is "not resolved yet", and wiping
      // the tab's org on that would undo it on every reload.
      if (user === null) this.setTabOrg(null);
      // Another account signing in with no sign-out between (two internal
      // admins on one browser) does not inherit this tab's org either. The
      // first account seen after a reload is not a switch.
      if (user && typeof this.lastUid === 'string' && this.lastUid !== user.uid) {
        this.setTabOrg(null);
      }
      if (user !== undefined) this.lastUid = user?.uid ?? null;
      this.disposeUserSub();
      this._userState.set(EMPTY_USER_STATE);
      this._userLoaded.set(false);
      this._userDocSeq.set(0);
      this.disposeOrgSub();
      this._activeOrg.set(null);
      this._activeOrgDocKey.set(null);
      this._activeRole.set(null);
      if (!user) return;
      this.subscribeUser(user.uid);
    });

    // Bind/unbind org-doc subscription to the active org and the token's
    // access to it (see `orgDocKey`).
    effect(() => {
      const key = this.orgDocKey();
      this.disposeOrgSub();
      this._activeOrg.set(null);
      this._activeOrgDocKey.set(null);
      if (key) {
        this.subscribeOrg(key);
      }
    });

    // Resolve role from the auth claim map. Claims lag the users doc until
    // the ID token is refreshed; `MembershipSessionService` (started by
    // `App`) forces that refresh when `orgIds` and the claim disagree.
    effect(() => {
      const orgId = this.activeOrgId();
      const roles = this.auth.claims().orgRoles ?? {};
      this._activeRole.set(orgId ? (roles[orgId] ?? null) : null);
    });

    this.destroyRef.onDestroy(() => {
      this.disposeUserSub();
      this.disposeOrgSub();
    });
  }

  /**
   * True iff the active-org role grants `perm`.
   *
   * ⚠️ A borrowed role (#253) INTERSECTS with the real one rather than
   * replacing it. Written the other way round, a lens would be a way to show
   * yourself controls your account cannot use — buttons that reach the server
   * and bounce. This way the mode has exactly one direction: it takes things
   * away, so the worst a wrong lens can do is hide something.
   */
  can(perm: OrgPermission): boolean {
    const real =
      this.auth.claims().internalAdmin === true ||
      hasPermission(this._activeRole() ?? undefined, perm);
    const borrowed = this.viewAs.role();
    if (borrowed === null) return real;
    return real && hasPermission(borrowed, perm);
  }

  /**
   * Is the per-org feature flag on for the ACTIVE org?
   *
   * Only `=== true` is on: absent, false, or the org doc not loaded yet are
   * all off. One rule for everyone — internal admins get no bypass, so what
   * a customer sees is what staff see when acting as that org. Reads the
   * org doc this service already observes; no second listener.
   */
  hasFeature(feature: OrgFeatureName): boolean {
    // Off until the loaded doc is the ACTIVE org's: right after a switch the
    // previous org's doc is still here, and its flag says nothing about this one.
    const id = this.activeOrgId();
    if (id === null || this.activeOrgDocId() !== id) return false;
    return this._activeOrg()?.features?.[feature] === true;
  }

  /**
   * Switch the active org. Persists the choice on the user doc so it
   * survives sign-in on a different device. The trigger then mirrors the
   * field into the custom claim.
   *
   * Internal admins can switch into any org for support purposes
   * ("act as"); regular users are restricted to their own memberships.
   */
  async setActiveOrg(orgId: string): Promise<void> {
    const user = this.auth.user();
    if (!user) throw new Error('Not signed in.');
    const isInternal = this.auth.claims().internalAdmin === true;
    if (!isInternal && !this.orgIds().includes(orgId)) {
      throw new Error('You are not a member of that org.');
    }
    // The switcher steers the tab you are operating it from, so it drops
    // this tab's override rather than writing under it — otherwise picking
    // an org would appear to do nothing at all.
    this.setTabOrg(null);
    await setDoc(
      doc(this.db, 'users', user.uid) as DocumentReference<UserOrgFields>,
      { activeOrgId: orgId } as Partial<UserOrgFields>,
      { merge: true },
    );
  }

  /**
   * Idempotent server call. Hidden behind the service so the activation
   * effect doesn't have to know which callable to invoke.
   */
  async markActivated(): Promise<void> {
    const callable = httpsCallable<unknown, { activated: number }>(this.fns, 'markMemberActivated');
    await callable({});
  }

  /**
   * Send this tab to an org, or `null` to put it back on the account's.
   *
   * Internal admins only — a member switching org has `setActiveOrg`, which
   * persists the choice, and a per-tab override would only give them a
   * context that silently disappears with the tab. It grants nothing either
   * way: the rules and the callables read the token, not this.
   */
  setTabOrg(orgId: string | null): void {
    if (orgId !== null && !this.isInternalAccount()) return;
    if (orgId !== null && !isDocumentId(orgId)) return;
    this._tabOrgId.set(orgId);
    try {
      if (orgId === null) sessionStorage.removeItem(TAB_ORG_KEY);
      else sessionStorage.setItem(TAB_ORG_KEY, orgId);
    } catch {
      /* A tab context we cannot remember across a reload is still one that
         works until then. */
    }
  }

  private subscribeUser(uid: string): void {
    const ref = doc(this.db, 'users', uid);
    this.unsubscribeUser = onSnapshot(
      ref,
      (snap) => {
        const data = (snap.data() ?? {}) as Partial<UserOrgFields>;
        const orgIds = Array.isArray(data.orgIds) ? [...data.orgIds] : [];
        // Internal admins can act as any org, even one they don't
        // belong to. Regular users fall back to their first membership
        // when activeOrgId points somewhere they no longer have access.
        const isInternal = this.auth.claims().internalAdmin === true;
        const persistedActive = typeof data.activeOrgId === 'string' ? data.activeOrgId : null;
        const activeOrgId =
          persistedActive && (isInternal || orgIds.includes(persistedActive))
            ? persistedActive
            : (orgIds[0] ?? null);
        this._userLoaded.set(true);
        this._userState.set({
          orgIds,
          activeOrgId,
          pendingActivation: data.pendingActivation === true,
          joinRequest: parseJoinRequest(data.joinRequest),
        });
        this._userDocSeq.update((n) => n + 1);
      },
      () => {
        this._userState.set(EMPTY_USER_STATE);
        this._userLoaded.set(true);
        // An unreadable mirror is not an empty one: nothing to compare.
        this._userDocSeq.set(0);
      },
    );
  }

  private subscribeOrg(key: OrgDocKey): void {
    const ref = doc(this.db, 'orgs', key.id);
    this.unsubscribeOrg = onSnapshot(
      ref,
      (snap) => {
        this._activeOrg.set(snap.exists() ? (snap.data() as Org) : null);
        this._activeOrgDocKey.set(key);
      },
      () => {
        this._activeOrg.set(null);
        this._activeOrgDocKey.set(key);
      },
    );
  }

  private disposeUserSub(): void {
    if (this.unsubscribeUser) {
      this.unsubscribeUser();
      this.unsubscribeUser = null;
    }
  }

  private disposeOrgSub(): void {
    if (this.unsubscribeOrg) {
      this.unsubscribeOrg();
      this.unsubscribeOrg = null;
    }
  }
}
